import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, DatePicker, Drawer, Form, Input, Popconfirm, Radio, Result, Segmented, Select, Space, Table, Tag, Typography, notification,
} from 'antd';
import { NotificationOutlined, PlusOutlined, ReloadOutlined, TeamOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import ModuleNav, { MODULE_NAVS } from '../../components/ModuleNav';
import lib from '../../assets/utils/lib';
import { usePermissions } from '../../assets/utils/usePermissions';

const { Title, Paragraph, Text } = Typography;
const PERMS = ['facility_notices.manage', 'admin.*'];

const STATE_TAG = {
  active: { color: 'green', label: 'Active' },
  scheduled: { color: 'blue', label: 'Scheduled' },
  draft: { color: 'default', label: 'Draft' },
  expired: { color: 'gold', label: 'Expired' },
  deactivated: { color: 'red', label: 'Deactivated' },
};

const FILTERS = [
  { key: 'facility_ids', label: 'Facilities', option: 'facilities', placeholder: 'Any facility' },
  { key: 'plan_ids', label: 'Plans', option: 'plans', placeholder: 'Any plan' },
  { key: 'facility_type_ids', label: 'Facility types', option: 'facility_types', placeholder: 'Any facility type' },
  { key: 'states', label: 'States / locations', option: 'states', placeholder: 'Any state' },
  { key: 'checkin_type_ids', label: 'Check-in types they provide', option: 'checkin_types', placeholder: 'Any check-in type' },
];

const fmt = (v) => (v ? dayjs(v).format('D MMM YYYY, HH:mm') : '—');

/**
 * Communications → Facility notices (Facility Portal Redesign §6).
 * Targeted, time-bound notices shown at the top of the Facility Portal.
 * Recipients are resolved on the server; this page previews them before publishing.
 */
export default function FacilityNoticesPage() {
  const { hasAnyPermission } = usePermissions();
  const allowed = hasAnyPermission(PERMS);

  const [rows, setRows] = useState([]);
  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('current');
  const [editing, setEditing] = useState(null); // null | {} | row
  const [saving, setSaving] = useState(false);
  const [recipients, setRecipients] = useState(null);
  const [form] = Form.useForm();
  const watched = Form.useWatch([], form);

  const load = useCallback(async () => {
    setLoading(true);
    const [list, opts] = await Promise.all([lib.facilityNotices.list(), lib.facilityNotices.options()]);
    if (list.error) notification.error({ message: 'Could not load notices', description: list.message });
    setRows(list?.data?.notices || []);
    if (opts?.data) setOptions(opts.data);
    setLoading(false);
  }, []);

  useEffect(() => { if (allowed) load(); }, [allowed, load]);

  const targetsFromForm = useCallback((values) => {
    const t = {};
    FILTERS.forEach((f) => { t[f.key] = values?.[f.key] || []; });
    return t;
  }, []);

  // Live recipient preview while the targeting is edited.
  const targetsKey = JSON.stringify(targetsFromForm(watched));
  useEffect(() => {
    if (!editing) return undefined;
    let cancelled = false;
    const id = setTimeout(async () => {
      const res = await lib.facilityNotices.recipients(JSON.parse(targetsKey));
      if (!cancelled) setRecipients(res.error ? { error: res.message } : res.data);
    }, 300);
    return () => { cancelled = true; clearTimeout(id); };
  }, [editing, targetsKey]);

  const openNew = () => {
    form.resetFields();
    form.setFieldsValue({ severity: 'info', period: [dayjs(), dayjs().add(7, 'day')] });
    setRecipients(null);
    setEditing({});
  };

  const openEdit = (row) => {
    form.resetFields();
    form.setFieldsValue({
      title: row.title,
      message: row.message,
      severity: row.severity,
      period: [dayjs(row.starts_at), dayjs(row.ends_at)],
      ...row.targets,
    });
    setRecipients(null);
    setEditing(row);
  };

  const save = async (publish) => {
    let values;
    try {
      values = await form.validateFields();
    } catch (_) {
      return;
    }
    const body = {
      title: values.title,
      message: values.message,
      severity: values.severity,
      starts_at: values.period[0].format('YYYY-MM-DD HH:mm:ss'),
      ends_at: values.period[1].format('YYYY-MM-DD HH:mm:ss'),
      targets: targetsFromForm(values),
    };
    setSaving(true);
    let res;
    if (editing?.id) {
      res = await lib.facilityNotices.update(editing.id, body);
      if (!res.error && publish && editing.status !== 'published') res = await lib.facilityNotices.publish(editing.id);
    } else {
      res = await lib.facilityNotices.create({ ...body, publish });
    }
    setSaving(false);
    if (res.error) {
      notification.error({ message: 'Could not save the notice', description: res.message });
      return;
    }
    notification.success({ message: publish || editing?.status === 'published' ? 'Notice saved and live for its period' : 'Draft saved' });
    setEditing(null);
    load();
  };

  const act = async (fn, id, ok) => {
    const res = await fn(id);
    if (res.error) {
      notification.error({ message: 'Action failed', description: res.message });
      return;
    }
    notification.success({ message: ok });
    load();
  };

  const labelFor = useMemo(() => {
    const maps = {};
    FILTERS.forEach((f) => {
      maps[f.key] = {};
      (options[f.option] || []).forEach((o) => { maps[f.key][String(o.value)] = o.label; });
    });
    return maps;
  }, [options]);

  const describeTargets = (targets = {}) => {
    const parts = FILTERS
      .filter((f) => (targets[f.key] || []).length)
      .map((f) => {
        const names = targets[f.key].map((v) => labelFor[f.key][String(v)] || v);
        const shown = names.slice(0, 3).join(' or ');
        return `${f.label}: ${shown}${names.length > 3 ? ` +${names.length - 3}` : ''}`;
      });
    return parts.length ? parts.join(' · and ') : 'All facilities';
  };

  const visible = rows.filter((r) => (
    view === 'current' ? ['active', 'scheduled', 'draft'].includes(r.state) : ['expired', 'deactivated'].includes(r.state)
  ));

  if (!allowed) {
    return <Result status="403" title="Not available" subTitle="Managing facility notices requires the facility_notices.manage permission." />;
  }

  const noFilters = recipients?.all_facilities;

  return (
    <div style={{ padding: 16 }}>
      <Space align="center" style={{ justifyContent: 'space-between', width: '100%', marginBottom: 8 }} wrap>
        <Title level={3} style={{ margin: 0 }}><NotificationOutlined /> Facility notices</Title>
        <Space>
          <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>Refresh</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>New notice</Button>
        </Space>
      </Space>
      <ModuleNav items={MODULE_NAVS.campaigns} />
      <Paragraph type="secondary">
        Notices appear at the top of every Facility Portal page for the facilities they target, from their start to their end time.
        Several can be live at once. Deactivating one removes it immediately and leaves the others alone.
      </Paragraph>

      <Segmented
        value={view}
        onChange={setView}
        style={{ marginBottom: 12 }}
        options={[
          { label: `Active, scheduled & drafts · ${rows.filter((r) => ['active', 'scheduled', 'draft'].includes(r.state)).length}`, value: 'current' },
          { label: `Expired & deactivated · ${rows.filter((r) => ['expired', 'deactivated'].includes(r.state)).length}`, value: 'past' },
        ]}
      />

      <Table
        size="small"
        rowKey="id"
        loading={loading}
        dataSource={visible}
        pagination={{ pageSize: 20 }}
        locale={{ emptyText: view === 'current' ? 'No current notices' : 'No past notices' }}
        columns={[
          {
            title: 'Notice',
            key: 'notice',
            render: (_, r) => (
              <div style={{ maxWidth: 560 }}>
                <Space size={6}>
                  {r.severity === 'warning' ? <Tag color="red">Important</Tag> : null}
                  <Text strong>{r.title}</Text>
                </Space>
                <div style={{ color: '#64748b', fontSize: 12, whiteSpace: 'pre-wrap' }}>{r.message}</div>
              </div>
            ),
          },
          {
            title: 'Recipients',
            key: 'targets',
            width: 280,
            render: (_, r) => <span style={{ fontSize: 12 }}>{describeTargets(r.targets)}</span>,
          },
          {
            title: 'Period',
            key: 'period',
            width: 220,
            render: (_, r) => <span style={{ fontSize: 12 }}>{fmt(r.starts_at)} → {fmt(r.ends_at)}</span>,
          },
          {
            title: 'Status',
            key: 'state',
            width: 110,
            render: (_, r) => <Tag color={STATE_TAG[r.state]?.color}>{STATE_TAG[r.state]?.label || r.state}</Tag>,
          },
          {
            title: '',
            key: 'actions',
            width: 230,
            render: (_, r) => (
              <Space size={4} wrap>
                <Button size="small" onClick={() => openEdit(r)}>Edit</Button>
                {r.status !== 'published' && r.state !== 'expired' ? (
                  <Popconfirm
                    title="Publish this notice?"
                    description={`It will show to: ${describeTargets(r.targets)}.`}
                    okText="Publish"
                    onConfirm={() => act(lib.facilityNotices.publish, r.id, 'Notice published')}
                  >
                    <Button size="small" type="primary">Publish</Button>
                  </Popconfirm>
                ) : null}
                {r.status === 'published' && r.state !== 'expired' ? (
                  <Popconfirm
                    title="Deactivate this notice?"
                    description="Facilities stop seeing it immediately."
                    okText="Deactivate"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => act(lib.facilityNotices.deactivate, r.id, 'Notice deactivated')}
                  >
                    <Button size="small" danger>Deactivate</Button>
                  </Popconfirm>
                ) : null}
              </Space>
            ),
          },
        ]}
      />

      <Drawer
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        width={Math.min(640, typeof window !== 'undefined' ? window.innerWidth : 640)}
        title={editing?.id ? 'Edit notice' : 'New facility notice'}
        destroyOnClose
        footer={(
          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            {editing?.status === 'published' ? (
              <Button type="primary" loading={saving} onClick={() => save(false)}>Save changes</Button>
            ) : (
              <>
                <Button loading={saving} onClick={() => save(false)}>Save draft</Button>
                <Button type="primary" loading={saving} onClick={() => save(true)}>
                  Publish{recipients && !recipients.error ? ` to ${recipients.count} ${recipients.count === 1 ? 'facility' : 'facilities'}` : ''}
                </Button>
              </>
            )}
          </Space>
        )}
      >
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item name="title" label="Title" rules={[{ required: true, whitespace: true, message: 'Add a title' }, { max: 160 }]}>
            <Input placeholder="e.g. Planned maintenance on Saturday" maxLength={160} />
          </Form.Item>
          <Form.Item name="message" label="Message" rules={[{ required: true, whitespace: true, message: 'Add the message' }, { max: 2000 }]}>
            <Input.TextArea rows={4} maxLength={2000} showCount placeholder="What facilities need to know" />
          </Form.Item>
          <Space size={16} wrap style={{ width: '100%' }}>
            <Form.Item name="severity" label="Style">
              <Radio.Group optionType="button" options={[{ label: 'Information', value: 'info' }, { label: 'Important', value: 'warning' }]} />
            </Form.Item>
            <Form.Item
              name="period"
              label={`Shown from → until (${options.timezone || 'Africa/Lagos'})`}
              rules={[{ required: true, message: 'Choose the start and end' }]}
            >
              <DatePicker.RangePicker showTime={{ format: 'HH:mm' }} format="D MMM YYYY HH:mm" />
            </Form.Item>
          </Space>

          <Title level={5} style={{ marginTop: 8 }}><TeamOutlined /> Recipients</Title>
          <Paragraph type="secondary" style={{ fontSize: 12 }}>
            Within a filter, a facility matches <strong>any</strong> of the choices; across filters it must match <strong>all</strong> of them.
            Leave a filter empty to not restrict by it. Facilities added later receive the notice only if they match.
          </Paragraph>
          {FILTERS.map((f) => (
            <Form.Item key={f.key} name={f.key} label={f.label} style={{ marginBottom: 12 }}>
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={f.placeholder}
                options={options[f.option] || []}
                maxTagCount="responsive"
              />
            </Form.Item>
          ))}

          {recipients?.error ? (
            <Alert type="error" showIcon message="Could not work out the recipients" description={recipients.error} />
          ) : recipients ? (
            <Alert
              type={noFilters ? 'warning' : recipients.count === 0 ? 'error' : 'info'}
              showIcon
              message={noFilters
                ? `No filters: this notice goes to every active facility (${recipients.count}).`
                : recipients.count === 0
                  ? 'No facility matches these filters.'
                  : `${recipients.count} of ${recipients.total_facilities} facilities match.`}
              description={!noFilters && recipients.sample?.length ? (
                <span style={{ fontSize: 12 }}>
                  {recipients.sample.slice(0, 12).map((s) => s.facility_name).join(', ')}
                  {recipients.count > 12 ? ` and ${recipients.count - 12} more` : ''}
                </span>
              ) : null}
            />
          ) : null}
        </Form>
      </Drawer>
    </div>
  );
}
